import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Dialog } from "../ui/Dialog"
import { CheckboxItem } from "../ui/Checkbox"
import { ProjectInfoRecord, ProjectResetOption } from "@/lib/catalog/types"
import { Button } from "../ui/Button"
import { popDialog, replaceDialog, resetDialogs } from "@/store/dialogs-state"
import { clearProject } from "@/store/project/project"
import { LoadingDialog } from "./LoadingDialog"
import { ProjectItem } from "@/store/project/projects-service"
import toast from "react-hot-toast"
import { projectItemToProjectInfo } from "@/store/project/projects-state"
import { isAbortError } from "@/utils/error"

export function ResetProjectDialog(props: {
	project?: ProjectItem
	projectInfo?: ProjectInfoRecord
	onFinish?: (selected: Set<ProjectResetOption>) => void
}) {
	const { t } = useTranslation("components")
	const [selected, setSelected] = useState<Set<ProjectResetOption>>(new Set())
	const projectInfo =
		props.projectInfo ?? (props.project ? projectItemToProjectInfo(props.project) : undefined)
	const toggleItem = (option: ProjectResetOption, isChecked: boolean) => {
		if (isChecked) {
			selected.add(option)
			setSelected(new Set(selected))
		} else {
			selected.delete(option)
			setSelected(new Set(selected))
		}
	}

	const description = () => {
		if (selected.has("edits") && selected.has("ratings")) {
			return t("projectResetDialog.descBoth")
		} else if (selected.has("edits")) {
			return t("projectResetDialog.descEdits")
		} else if (selected.has("ratings")) {
			return t("projectResetDialog.descRatings")
		} else {
			return t("projectResetDialog.selectWhatToReset")
		}
	}

	const reset = async () => {
		resetDialogs()

		if (!projectInfo) {
			return
		}

		replaceDialog(LoadingDialog, { title: t("projectResetDialog.resetting") })

		try {
			await clearProject(projectInfo, [...selected])
			resetDialogs()
			props.onFinish?.(selected)
		} catch (error) {
			resetDialogs()
			if (!isAbortError(error)) {
				toast.error(t("projectResetDialog.failedToReset"))
			}
			console.error("Failed to reset project", error)
		}
	}

	return (
		<Dialog trackingViewName="reset_project_dialog" contentSize="sm-h-auto" disableClose>
			<div className="flex w-full flex-col items-start justify-start space-y-6">
				<div className="flex flex-col space-y-2">
					<div className="text-start text-md font-bold">
						{t("projectResetDialog.resetProject")}
					</div>
					<div className="text-start text-sm text-base-300">{description()}</div>
				</div>
				<div className="flex w-full flex-col items-start justify-start space-y-6">
					<div className="flex w-full flex-row justify-between space-x-3">
						<div className="flex w-1/3 flex-col items-start justify-start space-y-3">
							<CheckboxItem
								trackingLabel="edits"
								id="edits"
								checkedState={selected.has("edits")}
								content={
									<div className="flex flex-row items-center space-x-1">
										{t("projectResetDialog.edits")}
									</div>
								}
								onCheckChanged={(key, isChecked) => {
									toggleItem(key, isChecked)
								}}
							/>
							<CheckboxItem
								trackingLabel="ratings"
								id="ratings"
								checkedState={selected.has("ratings")}
								content={
									<div className="flex flex-row items-center space-x-1">
										{t("projectResetDialog.ratingsLabelsFlags")}
									</div>
								}
								onCheckChanged={(key, isChecked) => {
									toggleItem(key, isChecked)
								}}
							/>
						</div>
					</div>
					<div className="flex w-full flex-row justify-end space-x-2">
						<Button trackingLabel="cancel" size="sm" onClick={() => popDialog()}>
							{t("common:action.cancel")}
						</Button>
						<Button
							trackingLabel="confirm"
							size="sm"
							onClick={reset}
							disabled={selected.size === 0}
							variant="destructive"
						>
							{t("projectResetDialog.resetProject")}
						</Button>
					</div>
				</div>
			</div>
		</Dialog>
	)
}
